import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { apiService } from '../services/api';
import { 
  Search, 
  Plus, 
  Building2, 
  TrendingUp, 
  Edit,
  Trash2,
  X,
  Save,
  CheckCircle
} from 'lucide-react';

const AutoTrading = () => {
  const navigate = useNavigate();
  // Check for tab parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const urlTab = urlParams.get('tab');
  const [activeTab, setActiveTab] = useState(urlTab || 'Exchanges');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data states
  const [exchanges, setExchanges] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Error modal states
  const [errorModal, setErrorModal] = useState({
    show: false,
    title: '',
    message: '',
    details: [],
    type: 'error' // 'error', 'warning', 'info'
  });
  
  // Confirmation modal states
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    type: 'danger' // 'danger', 'warning', 'info'
  });
  
  // Ticker validation and suggestions states
  const [validation, setValidation] = useState({
    isValidating: false,
    isValid: null,
    message: '',
    data: null,
    warning: null,
    error_type: null
  });
  const [originalTicker, setOriginalTicker] = useState('');
  
  const [suggestions, setSuggestions] = useState({
    loading: false,
    items: [],
    showDropdown: false,
    query: ''
  });

  const [stats, setStats] = useState({
    totalExchanges: 0,
    totalInstruments: 0
  });

  const tabs = ['Exchanges', 'Instruments'];

  const statsConfig = [
    {
      title: 'Total Exchanges',
      value: stats.totalExchanges,
      icon: Building2,
      color: 'text-blue-500',
      description: 'Foundation: what exchanges are supported'
    },
    {
      title: 'Total Instruments',
      value: stats.totalInstruments,
      icon: TrendingUp,
      color: 'text-purple-500',
      description: 'What tradable items exist within those exchanges'
    }
  ];

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, []);


  // Validate ticker when it changes (debounced)
  useEffect(() => {
    const validateTicker = async (ticker, exchangeId) => {
      if (!ticker || ticker.length < 1 || !exchangeId) {
        setValidation({
          isValidating: false,
          isValid: null,
          message: '',
          data: null,
          warning: null,
          error_type: null
        });
        return;
      }
      
      setValidation(prev => ({
        ...prev,
        isValidating: true,
        message: 'Validating ticker...'
      }));
      
      try {
        const response = await apiService.validateTicker({
          ticker: ticker.toUpperCase(),
          exchange_id: exchangeId,
          ...(selectedItem && { instrument_id: selectedItem.id })
        });
        
        const { valid, message, data, warning, error_type } = response.data;
        
        setValidation({
          isValidating: false,
          isValid: valid,
          message: message,
          data: data,
          warning: warning,
          error_type: error_type
        });
        
        // Auto-populate fields if validation returns data
        if (valid && data) {
          setFormData(prev => ({
            ...prev,
            name: data.name || prev.name,
            currency: data.currency || 'USD'
          }));
        }
        
      } catch (error) {
        console.error('Validation error:', error);
        setValidation({
          isValidating: false,
          isValid: false,
          message: `Validation failed: ${error.response?.data?.message || error.message}`,
          data: null,
          warning: null,
          error_type: null
        });
      }
    };

    const searchTickerSuggestions = async (query, exchangeId) => {
      if (!query || query.length < 1 || !exchangeId) {
        setSuggestions(prev => ({
          ...prev,
          items: [],
          showDropdown: false,
          query: ''
        }));
        return;
      }
      
      setSuggestions(prev => ({
        ...prev,
        loading: true,
        query: query
      }));
      
      try {
        const response = await apiService.searchTickers({
          query: query,
          exchange_id: exchangeId,
          limit: 10
        });
        
        const { suggestions: items } = response.data;
        
        setSuggestions(prev => ({
          ...prev,
          loading: false,
          items: items || [],
          showDropdown: items && items.length > 0
        }));
        
      } catch (error) {
        console.error('Suggestions error:', error);
        setSuggestions(prev => ({
          ...prev,
          loading: false,
          items: [],
          showDropdown: false
        }));
      }
    };

    if (!formData.ticker || !formData.exchange_id) return;

    const validationTimer = setTimeout(() => {
      // In edit mode, only validate if ticker has changed from original
      if (modalMode === 'edit' && formData.ticker === originalTicker) {
        return; // Skip validation if ticker hasn't changed in edit mode
      }
      validateTicker(formData.ticker, formData.exchange_id);
    }, 500); // 500ms delay for validation
    
    const suggestionsTimer = setTimeout(() => {
      if (formData.ticker.length >= 1) {
        searchTickerSuggestions(formData.ticker, formData.exchange_id);
      }
    }, 300); // 300ms delay for suggestions (faster)
    
    return () => {
      clearTimeout(validationTimer);
      clearTimeout(suggestionsTimer);
    };
  }, [formData.ticker, formData.exchange_id, modalMode, originalTicker]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [exchangesRes, instrumentsRes, symbolsRes] = await Promise.all([
        apiService.getExchanges().catch(() => ({ data: [] })),
        apiService.getInstruments().catch(() => ({ data: [] })),
        apiService.getSymbols?.().catch(() => ({ data: [] })) || Promise.resolve({ data: [] })
      ]);

      const exchangesData = exchangesRes.data.results || exchangesRes.data || [];
      const instrumentsData = instrumentsRes.data.results || instrumentsRes.data || [];
      const symbolsData = symbolsRes.data.results || symbolsRes.data || [];

      setExchanges(exchangesData);
      setInstruments(instrumentsData);
      setSymbols(symbolsData);

      setStats({
        totalExchanges: exchangesData.length,
        totalInstruments: instrumentsData.length
      });

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'Exchanges': return exchanges;
      case 'Instruments': return instruments;
      default: return [];
    }
  };

  const getFilteredData = () => {
    const data = getCurrentData();
    if (!searchTerm) return data;
    
    return data.filter(item => {
      switch (activeTab) {
        case 'Exchanges':
          return item.name?.toLowerCase().includes(searchTerm.toLowerCase());
        case 'Instruments':
          return item.ticker?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 item.name?.toLowerCase().includes(searchTerm.toLowerCase());
        default:
          return true;
      }
    });
  };

  const openModal = async (mode, item = null) => {
    setModalMode(mode);
    setSelectedItem(item);
    setFormErrors({});
    
    // Reset validation and suggestions states
    setValidation({
      isValidating: false,
      isValid: null,
      message: '',
      data: null,
      warning: null,
      error_type: null
    });
    setSuggestions({
      loading: false,
      items: [],
      showDropdown: false,
      query: ''
    });
    
    if (mode === 'create') {
      setFormData(getEmptyFormData());
      setOriginalTicker('');
      setShowModal(true);
    } else {
      // For edit mode, handle different tabs
      if (activeTab === 'Instruments') {
        // Fetch complete instrument data
        try {
          const response = await apiService.getInstrument(item.id);
          const fullItem = response.data;
          
          // Map nested objects to flat IDs for instruments
          const mappedFormData = {
            id: fullItem.id,
            symbol_id: (fullItem.symbol?.id || fullItem.symbol_id || '').toString(),
            exchange_id: (fullItem.exchange?.id || fullItem.exchange_id || '').toString(),
            ticker: fullItem.ticker || '',
            name: fullItem.name || '',
            instrument_type: fullItem.instrument_type || 'spot',
            description: fullItem.description || '',
            currency: fullItem.currency || 'USD',
            leverage_allowed: fullItem.leverage_allowed || false
          };
          
          setFormData(mappedFormData);
          setOriginalTicker(mappedFormData.ticker || '');
          setShowModal(true);
        } catch (error) {
          console.error('Error fetching complete instrument data:', error);
          alert('Error loading instrument details: ' + (error.response?.data?.detail || error.message));
        }
      } else {
        // For other tabs (like Exchanges), use the item directly
        setFormData(item);
        setShowModal(true);
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setFormData({});
    setFormErrors({});
    
    // Reset validation and suggestions states
    setValidation({
      isValidating: false,
      isValid: null,
      message: '',
      data: null,
      warning: null,
      error_type: null
    });
    setSuggestions({
      loading: false,
      items: [],
      showDropdown: false,
      query: ''
    });
  };
  
  const showErrorModal = (title, message, details = [], type = 'error') => {
    setErrorModal({
      show: true,
      title,
      message,
      details: Array.isArray(details) ? details : [details],
      type
    });
  };
  
  const closeErrorModal = () => {
    setErrorModal({
      show: false,
      title: '',
      message: '',
      details: [],
      type: 'error'
    });
  };
  
  const showConfirmModal = (title, message, onConfirm, confirmText = 'Delete', type = 'danger') => {
    setConfirmModal({
      show: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText: 'Cancel',
      type
    });
  };
  
  const closeConfirmModal = () => {
    setConfirmModal({
      show: false,
      title: '',
      message: '',
      onConfirm: null,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
  };

  const getEmptyFormData = () => {
    switch (activeTab) {
      case 'Exchanges':
        return {
          name: '',
          api_base_url: '',
          description: ''
        };
      case 'Instruments':
        return {
          ticker: '',
          name: '',
          symbol_id: '',
          exchange_id: '',
          instrument_type: 'spot',
          description: '',
          currency: 'USD',
          leverage_allowed: false
        };
      default:
        return {};
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormErrors({});

    // Additional validation for instruments
    if (activeTab === 'Instruments' && modalMode === 'create') {
      if (validation.isValid === false) {
        setFormErrors({ ticker: 'Ticker validation failed. Please use a valid ticker.' });
        setSubmitting(false);
        return;
      }
      if (validation.isValidating) {
        setFormErrors({ ticker: 'Please wait for ticker validation to complete.' });
        setSubmitting(false);
        return;
      }
    }

    try {
      let response;
      
      if (modalMode === 'create') {
        switch (activeTab) {
          case 'Exchanges':
            response = await apiService.createExchange(formData);
            setExchanges([...exchanges, response.data]);
            break;
          case 'Instruments':
            response = await apiService.createInstrument(formData);
            setInstruments([...instruments, response.data]);
            break;
        }
      } else {
        switch (activeTab) {
          case 'Exchanges':
            response = await apiService.updateExchange(selectedItem.id, formData);
            setExchanges(exchanges.map(ex => ex.id === selectedItem.id ? response.data : ex));
            break;
          case 'Instruments':
            response = await apiService.updateInstrument(selectedItem.id, formData);
            setInstruments(instruments.map(inst => inst.id === selectedItem.id ? response.data : inst));
            break;
        }
      }

      closeModal();
      // Refresh stats
      fetchAllData();
      
    } catch (error) {
      console.error('Error saving:', error);
      if (error.response?.data) {
        setFormErrors(error.response.data);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (item) => {
    const itemType = activeTab.slice(0, -1).toLowerCase();
    const itemName = item.name || item.ticker || 'item';
    
    showConfirmModal(
      `Delete ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}`,
      `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      () => performDelete(item)
    );
  };
  
  const performDelete = async (item) => {
    closeConfirmModal();

    try {
      switch (activeTab) {
        case 'Exchanges':
          await apiService.deleteExchange(item.id);
          setExchanges(exchanges.filter(ex => ex.id !== item.id));
          break;
        case 'Instruments':
          await apiService.deleteInstrument(item.id);
          setInstruments(instruments.filter(inst => inst.id !== item.id));
          break;
      }
      
      // Refresh stats
      fetchAllData();
      
    } catch (error) {
      console.error('Error deleting:', error);
      
      // Parse error details
      let errorTitle = 'Delete Failed';
      let errorMessage = 'Unable to delete this item.';
      let errorDetails = [];
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        // Handle specific error types
        if (errorData.used_in || errorData.dependencies) {
          errorTitle = 'Item Currently in Use';
          errorMessage = errorData.detail || `Cannot delete ${activeTab.slice(0, -1).toLowerCase()} because it is being used by:`;
          errorDetails = [...(errorData.used_in || errorData.dependencies || [])];
          
          // Add help text if provided
          if (errorData.help_text) {
            errorDetails.push(''); // Empty line for spacing
            errorDetails.push(`💡 ${errorData.help_text}`);
          }
        } else if (errorData.foreign_key_constraint) {
          errorTitle = 'Database Constraint Error';
          errorMessage = 'This item cannot be deleted because it is referenced by other data.';
          errorDetails = [errorData.foreign_key_constraint];
        }
      }
      
      showErrorModal(errorTitle, errorMessage, errorDetails, 'error');
    }
  };

  const renderModal = () => {
    if (!showModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-3">
              <h3 className="text-lg font-semibold text-white">
                {modalMode === 'create' ? `Add ${activeTab.slice(0, -1)}` : `Edit ${activeTab.slice(0, -1)}`}
              </h3>
            </div>
            <button onClick={closeModal} className="text-gray-400 hover:text-white">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {renderFormFields()}
            
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={closeModal}
                className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderFormFields = () => {
    switch (activeTab) {
      case 'Exchanges':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                required
              />
              {formErrors.name && <p className="text-red-400 text-xs mt-1">{formErrors.name[0]}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">API Base URL</label>
              <input
                type="url"
                value={formData.api_base_url || ''}
                onChange={(e) => setFormData({...formData, api_base_url: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder="https://api.example.com"
              />
              {formErrors.api_base_url && <p className="text-red-400 text-xs mt-1">{formErrors.api_base_url[0]}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                rows="3"
              />
            </div>
          </>
        );

      case 'Instruments':
        return (
          <>
            {/* Exchange Selection - First Field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Exchange *</label>
              <select
                value={formData.exchange_id || ''}
                onChange={(e) => {
                  const newExchangeId = e.target.value;
                  setFormData({
                    ...formData, 
                    exchange_id: newExchangeId,
                    // Clear ticker-related fields when exchange changes
                    ticker: '',
                    name: ''
                  });
                  // Clear validation and suggestions when exchange changes
                  setValidation({
                    isValidating: false,
                    isValid: null,
                    message: '',
                    data: null
                  });
                  setSuggestions({
                    loading: false,
                    items: [],
                    showDropdown: false,
                    query: ''
                  });
                }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                required
              >
                <option value="">Select Exchange</option>
                {exchanges.map(ex => (
                  <option key={ex.id} value={ex.id.toString()}>
                    {ex.name}
                  </option>
                ))}
              </select>
              {formErrors.exchange_id && <p className="text-red-400 text-xs mt-1">{formErrors.exchange_id[0]}</p>}
            </div>

            {/* Ticker Input with Live Validation and Suggestions */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-300 mb-1">Ticker Symbol *</label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.ticker || ''}
                  onChange={(e) => {
                    const newTicker = e.target.value.toUpperCase();
                    setFormData({...formData, ticker: newTicker});
                    // Clear ticker-specific errors
                    if (formErrors.ticker) {
                      setFormErrors(prev => ({...prev, ticker: null}));
                    }
                  }}
                  onFocus={() => {
                    if (formData.ticker && suggestions.items.length > 0) {
                      setSuggestions(prev => ({ ...prev, showDropdown: true }));
                    }
                  }}
                  onBlur={() => {
                    // Delay hiding to allow for clicks on suggestions
                    setTimeout(() => {
                      setSuggestions(prev => ({ ...prev, showDropdown: false }));
                    }, 200);
                  }}
                  className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    !formData.exchange_id ? "Select exchange first" : 
                    "e.g., AAPL, MSFT, SPY"
                  }
                  disabled={!formData.exchange_id}
                  required
                />
                {suggestions.loading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              
              {/* Suggestions Dropdown */}
              {suggestions.showDropdown && suggestions.items.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                  <div className="p-2 text-xs text-gray-400 border-b border-gray-600">
                    Suggestions for "{suggestions.query}"
                  </div>
                  {suggestions.items.map((suggestion, index) => (
                    <button
                      key={`${suggestion.ticker}-${index}`}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          ticker: suggestion.ticker,
                          name: suggestion.name || prev.name
                        }));
                        setSuggestions(prev => ({ ...prev, showDropdown: false }));
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-600 focus:bg-gray-600 text-white flex items-center justify-between border-l-4 border-transparent hover:border-blue-400 focus:border-blue-400"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-blue-400">{suggestion.ticker}</span>
                          {suggestion.exists && (
                            <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                              EXISTS
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-300 truncate">{suggestion.name}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Validation Status */}
              {validation.isValidating && (
                <div className="flex items-center mt-2 text-blue-400 text-sm">
                  <div className="animate-spin w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full mr-2"></div>
                  {validation.message}
                </div>
              )}
              
              {!validation.isValidating && validation.isValid === true && (
                <div className="flex items-center mt-2 text-green-400 text-sm">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  {validation.message}
                </div>
              )}
              
              {!validation.isValidating && validation.isValid === false && (
                <div className="flex items-start mt-2 text-red-400 text-sm">
                  <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                  </svg>
                  <div>
                    {validation.message}
                    {validation.error_type === 'duplicate' && (
                      <p className="text-xs mt-1">This instrument already exists in the system.</p>
                    )}
                    {validation.error_type === 'not_tradable' && (
                      <p className="text-xs mt-1">This ticker is not available for trading on the exchange.</p>
                    )}
                  </div>
                </div>
              )}
              
              {formErrors.ticker && <p className="text-red-400 text-xs mt-1">{formErrors.ticker[0]}</p>}
            </div>

            {/* Name Input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder="e.g., Apple Inc., Microsoft Corp."
                required
              />
              {formErrors.name && <p className="text-red-400 text-xs mt-1">{formErrors.name[0]}</p>}
            </div>

            {/* Symbol Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Symbol *</label>
              <select
                value={formData.symbol_id || ''}
                onChange={(e) => setFormData({...formData, symbol_id: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                required
              >
                <option value="">Select Symbol</option>
                {symbols.map(sym => <option key={sym.id} value={sym.id.toString()}>{sym.name} ({sym.label})</option>)}
              </select>
              {formErrors.symbol_id && <p className="text-red-400 text-xs mt-1">{formErrors.symbol_id[0]}</p>}
            </div>

            {/* Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
              <select
                value={formData.instrument_type || 'spot'}
                onChange={(e) => setFormData({...formData, instrument_type: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="spot">Stock/Spot</option>
                <option value="etf">ETF</option>
                <option value="future">Future</option>
                <option value="option">Option</option>
                <option value="mutual_fund">Mutual Fund</option>
              </select>
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Currency</label>
              <input
                type="text"
                value={formData.currency || 'USD'}
                onChange={(e) => setFormData({...formData, currency: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder="USD"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                rows="2"
                placeholder="Optional description of the instrument"
              />
            </div>

            {/* Leverage Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="leverage_allowed"
                checked={formData.leverage_allowed || false}
                onChange={(e) => setFormData({...formData, leverage_allowed: e.target.checked})}
                className="mr-2"
              />
              <label htmlFor="leverage_allowed" className="text-sm text-gray-300">Leverage Allowed</label>
            </div>
            
            {/* Help Text */}
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-blue-400 text-xs">
                <strong>💡 Tips:</strong> Select exchange first, then start typing ticker symbol for live suggestions. 
                Only valid, tradable instruments from the selected exchange can be added.
              </p>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const renderTable = () => {
    const data = getFilteredData();

    if (activeTab === 'Exchanges') {
      return (
        <table className="w-full">
          <thead className="bg-gray-700/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Exchange</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">API URL</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {loading ? (
              <tr><td colSpan="3" className="px-6 py-12 text-center text-gray-400">Loading...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan="3" className="px-6 py-12 text-center text-gray-400">No exchanges found</td></tr>
            ) : (
              data.map((item) => (
                <tr key={item.id} className="hover:bg-gray-700/30">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-white font-medium">{item.name}</div>
                      <div className="text-xs text-gray-400">{item.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-300">{item.api_base_url}</td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button onClick={() => openModal('edit', item)} className="text-blue-400 hover:text-blue-300">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => handleDelete(item)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      );
    }

    if (activeTab === 'Instruments') {
      return (
        <table className="w-full">
          <thead className="bg-gray-700/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                <div className="flex items-center space-x-2">
                  <span>Instrument</span>
                  <div className="group relative">
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      All instruments verified on exchanges
                    </div>
                  </div>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Exchange</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Symbol</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Type</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {loading ? (
              <tr><td colSpan="5" className="px-6 py-12 text-center text-gray-400">Loading...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan="5" className="px-6 py-12 text-center text-gray-400">No instruments found</td></tr>
            ) : (
              data.map((item) => (
                <tr key={item.id} className="hover:bg-gray-700/30">
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-white font-medium">{item.ticker}</span>
                        <div className="group relative">
                          <CheckCircle 
                            className="w-4 h-4 text-green-400" 
                            title="Verified tradable on exchange"
                          />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                            ✓ Verified tradable on {item.exchange_name || item.exchange?.name || 'exchange'}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">{item.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-300">{item.exchange_name || item.exchange?.name || '-'}</td>
                  <td className="px-6 py-4 text-gray-300">{item.symbol_name || item.symbol?.name || '-'}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs">{item.instrument_type}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button onClick={() => openModal('edit', item)} className="text-blue-400 hover:text-blue-300">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => handleDelete(item)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      );
    }

    return null;
  };

  const renderErrorModal = () => {
    if (!errorModal.show) return null;
    
    const iconColor = {
      error: 'text-red-400',
      warning: 'text-yellow-400', 
      info: 'text-blue-400'
    }[errorModal.type];
    
    const borderColor = {
      error: 'border-red-500',
      warning: 'border-yellow-500',
      info: 'border-blue-500'
    }[errorModal.type];
    
    const bgColor = {
      error: 'bg-red-500/10',
      warning: 'bg-yellow-500/10',
      info: 'bg-blue-500/10'
    }[errorModal.type];
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg mx-4 shadow-2xl">
          {/* Header */}
          <div className={`flex items-start space-x-4 p-4 rounded-lg ${bgColor} ${borderColor} border-2 mb-4`}>
            <div className="flex-shrink-0">
              {errorModal.type === 'error' && (
                <svg className={`w-6 h-6 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                </svg>
              )}
              {errorModal.type === 'warning' && (
                <svg className={`w-6 h-6 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
              )}
              {errorModal.type === 'info' && (
                <svg className={`w-6 h-6 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                </svg>
              )}
            </div>
            <div className="flex-1">
              <h3 className={`text-lg font-semibold ${iconColor}`}>{errorModal.title}</h3>
              <p className="text-gray-300 mt-1">{errorModal.message}</p>
            </div>
            <button
              onClick={closeErrorModal}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Details */}
          {errorModal.details.length > 0 && (
            <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-medium text-gray-300 mb-2">Details:</h4>
              <ul className="space-y-2">
                {errorModal.details.map((detail, index) => {
                  // Handle empty lines for spacing
                  if (detail === '') {
                    return <li key={index} className="h-2"></li>;
                  }
                  
                  // Handle help text with emoji
                  if (detail.startsWith('💡')) {
                    return (
                      <li key={index} className="text-sm text-blue-400 bg-blue-500/10 p-3 rounded-lg border-l-4 border-blue-500">
                        {detail}
                      </li>
                    );
                  }
                  
                  // Regular detail item
                  return (
                    <li key={index} className="flex items-start space-x-2 text-sm text-gray-400">
                      <span className="text-gray-500 mt-0.5">•</span>
                      <span>{detail}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={closeErrorModal}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderConfirmModal = () => {
    if (!confirmModal.show) return null;
    
    const buttonColor = {
      danger: 'bg-red-600 hover:bg-red-700',
      warning: 'bg-yellow-600 hover:bg-yellow-700',
      info: 'bg-blue-600 hover:bg-blue-700'
    }[confirmModal.type];
    
    const iconColor = {
      danger: 'text-red-400',
      warning: 'text-yellow-400',
      info: 'text-blue-400'
    }[confirmModal.type];
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
          <div className="flex items-start space-x-4 mb-6">
            <div className="flex-shrink-0">
              {confirmModal.type === 'danger' && (
                <svg className={`w-6 h-6 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 18.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              )}
              {confirmModal.type === 'warning' && (
                <svg className={`w-6 h-6 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
              )}
              {confirmModal.type === 'info' && (
                <svg className={`w-6 h-6 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                </svg>
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white">{confirmModal.title}</h3>
              <p className="text-gray-300 mt-2">{confirmModal.message}</p>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={closeConfirmModal}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              {confirmModal.cancelText}
            </button>
            <button
              onClick={() => confirmModal.onConfirm && confirmModal.onConfirm()}
              className={`px-6 py-2 text-white rounded-lg transition-colors font-medium ${buttonColor}`}
            >
              {confirmModal.confirmText}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col space-y-2">
          <h1 className="text-3xl font-bold text-white">Exchanges & Allocations</h1>
          <p className="text-gray-400">Manage supported exchanges and tradable instruments</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsConfig.map((stat, index) => (
            <div key={index} className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">{stat.title}</p>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">{stat.description}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex space-x-8 border-b border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                {tab === 'Exchanges' && <Building2 size={16} />}
                {tab === 'Instruments' && <TrendingUp size={16} />}
                <span>{tab}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Controls */}
        <div className="flex justify-between items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder={`Search ${activeTab.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button 
            onClick={() => openModal('create')}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus size={20} />
            <span>Add {activeTab.slice(0, -1)}</span>
          </button>
        </div>

        {/* Table */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-xl font-semibold text-white">All {activeTab}</h3>
          </div>
          
          <div className="overflow-x-auto">
            {renderTable()}
          </div>
        </div>

        {/* Modal */}
        {renderModal()}
        
        {/* Error Modal */}
        {renderErrorModal()}
        
        {/* Confirmation Modal */}
        {renderConfirmModal()}
      </div>
    </Layout>
  );
};

export default AutoTrading;